// pages/Bancos.jsx
import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import api from '../services/api';

const hoy  = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santo_Domingo' });
const mes1 = hoy.substring(0, 8) + '01';

const fmtM = v => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', minimumFractionDigits: 2 }).format(parseFloat(v) || 0);
const fmtF = v => v ? new Date(v + 'T00:00:00').toLocaleDateString('es-DO') : '—';

const TIPOS_MOV = [
  { key: 'deposito',     label: 'Depósito',           esEntrada: true  },
  { key: 'retiro',       label: 'Retiro',              esEntrada: false },
  { key: 'transferencia',label: 'Transferencia',       esEntrada: null  },
  { key: 'nota_debito',  label: 'Nota de Débito',      esEntrada: false },
  { key: 'nota_credito', label: 'Nota de Crédito',     esEntrada: true  },
  { key: 'ajuste',       label: 'Ajuste',              esEntrada: null  },
];

const sx = {
  page:     { padding: '2rem', maxWidth: 1100, margin: '0 auto', fontFamily: 'Inter, sans-serif' },
  titulo:   { fontSize: '1.5rem', fontWeight: 800, color: '#1a1a2e', margin: 0 },
  card:     { background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem' },
  tabBar:   { display: 'flex', gap: 4, marginBottom: '1.5rem', borderBottom: '2px solid #e9ecef', paddingBottom: 0 },
  tab:      (a) => ({ padding: '0.65rem 1.25rem', border: 'none', borderBottom: a ? '3px solid #0066cc' : '3px solid transparent', background: 'none', cursor: 'pointer', fontWeight: a ? 700 : 500, color: a ? '#0066cc' : '#6c757d', fontSize: '0.9rem', marginBottom: -2 }),
  th:       { padding: '0.6rem 0.75rem', background: '#f1f3f5', fontWeight: 700, fontSize: '0.8rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' },
  td:       { padding: '0.5rem 0.75rem', fontSize: '0.85rem', borderBottom: '1px solid #f1f3f5' },
  input:    { width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #ced4da', borderRadius: 6, fontSize: '0.875rem', boxSizing: 'border-box' },
  select:   { width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #ced4da', borderRadius: 6, fontSize: '0.875rem', background: '#fff', boxSizing: 'border-box' },
  label:    { fontSize: '0.82rem', fontWeight: 600, color: '#495057', marginBottom: 4, display: 'block' },
  btnPrim:  { background: '#0066cc', color: '#fff', border: 'none', borderRadius: 8, padding: '0.6rem 1.25rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.875rem' },
  btnSec:   { background: '#fff', color: '#495057', border: '1px solid #ced4da', borderRadius: 8, padding: '0.6rem 1.25rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' },
  btnGreen: { background: '#28a745', color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 1rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem' },
  grid2:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  grid3:    { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' },
  saldo:    (v) => ({ fontSize: '1.4rem', fontWeight: 900, color: v >= 0 ? '#166534' : '#991b1b' }),
};

function Campo({ label, children }) {
  return <div style={{ marginBottom: '0.75rem' }}><label style={sx.label}>{label}</label>{children}</div>;
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Bancos() {
  const [tab, setTab]               = useState('cuentas');
  const [cuentas, setCuentas]       = useState([]);
  const [cuentasContables, setCuentasContables] = useState([]);
  const [loading, setLoading]       = useState(false);

  const cargarCuentas = useCallback(async () => {
    setLoading(true);
    try {
      const [rB, rC] = await Promise.all([
        api.get('/bancos/cuentas'),
        api.get('/contabilidad/cuentas'),
      ]);
      setCuentas(rB.data.cuentas || []);
      const aplanar = (nodos) => nodos.flatMap(n => [n, ...aplanar(n.hijos || [])]);
      const todas = aplanar(rC.data.cuentas || []).filter(c => c.acepta_movimientos);
      setCuentasContables(todas);
    } catch (e) { Swal.fire('Error', 'No se pudieron cargar las cuentas bancarias', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargarCuentas(); }, [cargarCuentas]);

  return (
    <div style={sx.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={sx.titulo}>Bancos y Tesorería</h1>
          <p style={{ color: '#6c757d', margin: '4px 0 0', fontSize: '0.9rem' }}>Gestión de cuentas bancarias, movimientos y conciliación</p>
        </div>
      </div>

      {/* Resumen de saldos */}
      {cuentas.length > 0 && (
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {cuentas.map(c => (
            <div key={c.id} style={{ ...sx.card, marginBottom: 0, flex: '1 1 200px', minWidth: 180 }}>
              <div style={{ fontSize: '0.78rem', color: '#6c757d', fontWeight: 600, marginBottom: 4 }}>{c.banco}</div>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: 6 }}>{c.nombre}</div>
              <div style={sx.saldo(parseFloat(c.saldo_actual))}>{fmtM(c.saldo_actual)}</div>
              <div style={{ fontSize: '0.75rem', color: '#adb5bd', marginTop: 4 }}>{c.tipo} · {c.moneda}</div>
            </div>
          ))}
          <div style={{ ...sx.card, marginBottom: 0, flex: '1 1 200px', minWidth: 180, background: '#f0fdf4' }}>
            <div style={{ fontSize: '0.78rem', color: '#6c757d', fontWeight: 600, marginBottom: 4 }}>TOTAL TESORERÍA</div>
            <div style={sx.saldo(cuentas.reduce((s, c) => s + parseFloat(c.saldo_actual), 0))}>
              {fmtM(cuentas.reduce((s, c) => s + parseFloat(c.saldo_actual), 0))}
            </div>
          </div>
        </div>
      )}

      <div style={sx.tabBar}>
        {[['cuentas','Cuentas Bancarias'],['movimientos','Movimientos'],['conciliacion','Conciliación']].map(([k, l]) => (
          <button key={k} style={sx.tab(tab === k)} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'cuentas'      && <TabCuentas      cuentas={cuentas} cuentasContables={cuentasContables} onRefresh={cargarCuentas} />}
      {tab === 'movimientos'  && <TabMovimientos   cuentas={cuentas} cuentasContables={cuentasContables} onRefresh={cargarCuentas} />}
      {tab === 'conciliacion' && <TabConciliacion  cuentas={cuentas} />}
    </div>
  );
}

// ─── Tab Cuentas ──────────────────────────────────────────────────────────────
function TabCuentas({ cuentas, cuentasContables, onRefresh }) {
  const VACIA = { nombre: '', banco: '', numero_cuenta: '', tipo: 'corriente', moneda: 'DOP', saldo_inicial: '', cuenta_contable_id: '', descripcion: '' };
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState(VACIA);
  const [edit, setEdit]   = useState(null);

  const abrir = (c = null) => { setEdit(c); setForm(c ? { ...c, cuenta_contable_id: c.cuenta_contable_id || '' } : VACIA); setModal(true); };

  const guardar = async () => {
    if (!form.nombre || !form.banco) return Swal.fire('Atención', 'Nombre y banco son requeridos', 'warning');
    try {
      if (edit) await api.put(`/bancos/cuentas/${edit.id}`, form);
      else      await api.post('/bancos/cuentas', form);
      setModal(false);
      onRefresh();
      Swal.fire({ icon: 'success', title: edit ? 'Cuenta actualizada' : 'Cuenta creada', timer: 1600, showConfirmButton: false });
    } catch (e) { Swal.fire('Error', e.response?.data?.mensaje || 'Error', 'error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button onClick={() => abrir()} style={sx.btnPrim}>+ Nueva Cuenta</button>
      </div>
      <div style={{ ...sx.card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['Nombre','Banco','Número','Tipo','Moneda','Cuenta Contable','Saldo Actual',''].map(h => <th key={h} style={sx.th}>{h}</th>)}</tr></thead>
          <tbody>
            {cuentas.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>No hay cuentas bancarias registradas</td></tr>
            ) : cuentas.map((c, i) => (
              <tr key={c.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                <td style={{ ...sx.td, fontWeight: 700 }}>{c.nombre}</td>
                <td style={sx.td}>{c.banco}</td>
                <td style={{ ...sx.td, fontFamily: 'monospace' }}>{c.numero_cuenta || '—'}</td>
                <td style={sx.td}>{c.tipo}</td>
                <td style={sx.td}>{c.moneda}</td>
                <td style={{ ...sx.td, fontSize: '0.78rem' }}>{c.cuentaContable ? `${c.cuentaContable.codigo} ${c.cuentaContable.nombre}` : '—'}</td>
                <td style={{ ...sx.td, textAlign: 'right', fontWeight: 700, color: parseFloat(c.saldo_actual) >= 0 ? '#166534' : '#991b1b' }}>{fmtM(c.saldo_actual)}</td>
                <td style={sx.td}><button onClick={() => abrir(c)} style={sx.btnSec}>Editar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '2rem', width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ margin: '0 0 1.5rem', fontSize: '1.1rem', fontWeight: 700 }}>{edit ? 'Editar cuenta' : 'Nueva cuenta bancaria'}</h2>
            <div style={sx.grid2}>
              <Campo label="Nombre *"><input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} style={sx.input} /></Campo>
              <Campo label="Banco *"><input value={form.banco} onChange={e => setForm(f => ({ ...f, banco: e.target.value }))} style={sx.input} /></Campo>
            </div>
            <div style={sx.grid2}>
              <Campo label="Número de Cuenta"><input value={form.numero_cuenta} onChange={e => setForm(f => ({ ...f, numero_cuenta: e.target.value }))} style={sx.input} /></Campo>
              <Campo label="Tipo">
                <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} style={sx.select}>
                  <option value="corriente">Corriente</option>
                  <option value="ahorros">Ahorros</option>
                  <option value="inversion">Inversión</option>
                </select>
              </Campo>
            </div>
            <div style={sx.grid2}>
              <Campo label="Moneda">
                <select value={form.moneda} onChange={e => setForm(f => ({ ...f, moneda: e.target.value }))} style={sx.select}>
                  <option value="DOP">DOP — Peso Dominicano</option>
                  <option value="USD">USD — Dólar</option>
                  <option value="EUR">EUR — Euro</option>
                </select>
              </Campo>
              <Campo label="Saldo Inicial"><input type="number" step="0.01" value={form.saldo_inicial} onChange={e => setForm(f => ({ ...f, saldo_inicial: e.target.value }))} style={sx.input} /></Campo>
            </div>
            <Campo label="Cuenta Contable (Plan de Cuentas)">
              <select value={form.cuenta_contable_id} onChange={e => setForm(f => ({ ...f, cuenta_contable_id: e.target.value }))} style={sx.select}>
                <option value="">— Sin vincular —</option>
                {cuentasContables.map(c => <option key={c.id} value={c.id}>{c.codigo} – {c.nombre}</option>)}
              </select>
            </Campo>
            <Campo label="Descripción"><input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} style={sx.input} /></Campo>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => setModal(false)} style={sx.btnSec}>Cancelar</button>
              <button onClick={guardar} style={sx.btnPrim}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab Movimientos ──────────────────────────────────────────────────────────
function TabMovimientos({ cuentas, cuentasContables, onRefresh }) {
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [modal, setModal]             = useState(false);
  const [filtro, setFiltro]           = useState({ cuenta_id: '', fecha_desde: mes1, fecha_hasta: hoy });
  const VACIO = { cuenta_bancaria_id: '', fecha: hoy, tipo: 'deposito', monto: '', descripcion: '', referencia: '', contra_cuenta_id: '', cuenta_destino_id: '' };
  const [form, setForm] = useState(VACIO);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filtro).filter(([,v]) => v)));
      const r = await api.get(`/bancos/movimientos?${params}`);
      setMovimientos(r.data.movimientos || []);
    } catch (e) { Swal.fire('Error', 'No se pudieron cargar los movimientos', 'error'); }
    finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { cargar(); }, [cargar]);

  const tipoMov = TIPOS_MOV.find(t => t.key === form.tipo);
  const esTransferencia = form.tipo === 'transferencia';
  const necesitaContra  = !esTransferencia;

  const guardar = async () => {
    if (!form.cuenta_bancaria_id || !form.tipo || !form.monto)
      return Swal.fire('Atención', 'Cuenta, tipo y monto son requeridos', 'warning');
    if (necesitaContra && !form.contra_cuenta_id)
      return Swal.fire('Atención', 'Selecciona la cuenta contable de contrapartida', 'warning');
    if (esTransferencia && !form.cuenta_destino_id)
      return Swal.fire('Atención', 'Selecciona la cuenta bancaria destino', 'warning');
    try {
      await api.post('/bancos/movimientos', form);
      setModal(false); setForm(VACIO); cargar(); onRefresh();
      Swal.fire({ icon: 'success', title: 'Movimiento registrado', timer: 1600, showConfirmButton: false });
    } catch (e) { Swal.fire('Error', e.response?.data?.mensaje || 'Error', 'error'); }
  };

  const colorTipo = (tipo) => {
    if (['deposito','nota_credito'].includes(tipo)) return '#166534';
    if (['retiro','nota_debito'].includes(tipo))    return '#991b1b';
    return '#1d4ed8';
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filtro.cuenta_id} onChange={e => setFiltro(f => ({ ...f, cuenta_id: e.target.value }))} style={{ ...sx.select, width: 'auto' }}>
            <option value="">Todas las cuentas</option>
            {cuentas.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
          <input type="date" value={filtro.fecha_desde} onChange={e => setFiltro(f => ({ ...f, fecha_desde: e.target.value }))} style={{ ...sx.input, width: 'auto' }} />
          <span style={{ color: '#6c757d' }}>—</span>
          <input type="date" value={filtro.fecha_hasta} onChange={e => setFiltro(f => ({ ...f, fecha_hasta: e.target.value }))} style={{ ...sx.input, width: 'auto' }} />
        </div>
        <button onClick={() => setModal(true)} style={sx.btnPrim}>+ Nuevo Movimiento</button>
      </div>

      <div style={{ ...sx.card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['Fecha','Cuenta','Tipo','Monto','Descripción','Referencia','Estado'].map(h => <th key={h} style={sx.th}>{h}</th>)}</tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>Cargando...</td></tr>
            ) : movimientos.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>No hay movimientos en el período</td></tr>
            ) : movimientos.map((m, i) => (
              <tr key={m.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                <td style={sx.td}>{fmtF(m.fecha)}</td>
                <td style={{ ...sx.td, fontSize: '0.8rem' }}>{m.cuenta?.nombre || '—'}</td>
                <td style={{ ...sx.td, fontWeight: 700, color: colorTipo(m.tipo) }}>{TIPOS_MOV.find(t => t.key === m.tipo)?.label || m.tipo}</td>
                <td style={{ ...sx.td, textAlign: 'right', fontWeight: 700, color: colorTipo(m.tipo) }}>{fmtM(m.monto)}</td>
                <td style={{ ...sx.td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.descripcion || '—'}</td>
                <td style={{ ...sx.td, fontFamily: 'monospace', fontSize: '0.78rem' }}>{m.referencia || '—'}</td>
                <td style={sx.td}>
                  <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 700, background: m.estado === 'conciliado' ? '#dcfce7' : '#fef9c3', color: m.estado === 'conciliado' ? '#166534' : '#854d0e' }}>
                    {m.estado === 'conciliado' ? 'Conciliado' : 'Pendiente'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '2rem', width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ margin: '0 0 1.5rem', fontSize: '1.1rem', fontWeight: 700 }}>Nuevo Movimiento Bancario</h2>
            <div style={sx.grid2}>
              <Campo label="Cuenta Bancaria *">
                <select value={form.cuenta_bancaria_id} onChange={e => setForm(f => ({ ...f, cuenta_bancaria_id: e.target.value }))} style={sx.select}>
                  <option value="">— Seleccionar —</option>
                  {cuentas.map(c => <option key={c.id} value={c.id}>{c.nombre} ({fmtM(c.saldo_actual)})</option>)}
                </select>
              </Campo>
              <Campo label="Tipo *">
                <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} style={sx.select}>
                  {TIPOS_MOV.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </Campo>
            </div>
            <div style={sx.grid2}>
              <Campo label="Fecha *"><input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} style={sx.input} /></Campo>
              <Campo label="Monto *"><input type="number" step="0.01" min="0" value={form.monto} onChange={e => setForm(f => ({ ...f, monto: e.target.value }))} style={sx.input} /></Campo>
            </div>

            {esTransferencia ? (
              <Campo label="Cuenta Destino *">
                <select value={form.cuenta_destino_id} onChange={e => setForm(f => ({ ...f, cuenta_destino_id: e.target.value }))} style={sx.select}>
                  <option value="">— Seleccionar cuenta destino —</option>
                  {cuentas.filter(c => String(c.id) !== String(form.cuenta_bancaria_id)).map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </Campo>
            ) : (
              <Campo label="Cuenta Contable de Contrapartida *">
                <select value={form.contra_cuenta_id} onChange={e => setForm(f => ({ ...f, contra_cuenta_id: e.target.value }))} style={sx.select}>
                  <option value="">— Seleccionar cuenta —</option>
                  {cuentasContables.map(c => <option key={c.id} value={c.id}>{c.codigo} – {c.nombre}</option>)}
                </select>
              </Campo>
            )}

            <div style={sx.grid2}>
              <Campo label="Descripción"><input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} style={sx.input} /></Campo>
              <Campo label="Referencia (No. cheque, etc.)"><input value={form.referencia} onChange={e => setForm(f => ({ ...f, referencia: e.target.value }))} style={sx.input} /></Campo>
            </div>

            {tipoMov && (
              <div style={{ background: '#f0f4ff', borderRadius: 6, padding: '0.6rem 0.9rem', fontSize: '0.82rem', color: '#1d4ed8', marginBottom: '1rem' }}>
                Asiento: {esTransferencia ? 'DR banco destino / CR banco origen' : tipoMov.esEntrada ? 'DR cuenta banco / CR contrapartida' : 'DR contrapartida / CR cuenta banco'}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(false)} style={sx.btnSec}>Cancelar</button>
              <button onClick={guardar} style={sx.btnPrim}>Registrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab Conciliación ─────────────────────────────────────────────────────────
function TabConciliacion({ cuentas }) {
  const [cuentaId, setCuentaId]     = useState('');
  const [pendientes, setPendientes] = useState([]);
  const [seleccion, setSeleccion]   = useState(new Set());
  const [loading, setLoading]       = useState(false);

  const cargar = async () => {
    if (!cuentaId) return;
    setLoading(true);
    try {
      const r = await api.get(`/bancos/movimientos?cuenta_id=${cuentaId}&estado=pendiente`);
      setPendientes(r.data.movimientos || []);
      setSeleccion(new Set());
    } catch (e) { Swal.fire('Error', 'Error al cargar', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, [cuentaId]);

  const toggle = (id) => setSeleccion(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSeleccion(s => s.size === pendientes.length ? new Set() : new Set(pendientes.map(m => m.id)));

  const conciliar = async () => {
    if (seleccion.size === 0) return Swal.fire('Atención', 'Selecciona al menos un movimiento', 'warning');
    const conf = await Swal.fire({
      title: `Conciliar ${seleccion.size} movimiento(s)`,
      text: 'Los movimientos seleccionados quedarán marcados como conciliados.',
      icon: 'question', showCancelButton: true, confirmButtonText: 'Conciliar', confirmButtonColor: '#166534',
    });
    if (!conf.isConfirmed) return;
    try {
      await api.post('/bancos/movimientos/conciliar-lote', { ids: [...seleccion] });
      Swal.fire({ icon: 'success', title: `${seleccion.size} movimiento(s) conciliados`, timer: 1800, showConfirmButton: false });
      cargar();
    } catch (e) { Swal.fire('Error', e.response?.data?.mensaje || 'Error', 'error'); }
  };

  const colorTipo = (tipo) => ['deposito','nota_credito'].includes(tipo) ? '#166534' : '#991b1b';

  return (
    <div>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={sx.label}>Cuenta bancaria</label>
          <select value={cuentaId} onChange={e => setCuentaId(e.target.value)} style={sx.select}>
            <option value="">— Seleccionar cuenta —</option>
            {cuentas.map(c => <option key={c.id} value={c.id}>{c.nombre} — {c.banco}</option>)}
          </select>
        </div>
        {seleccion.size > 0 && (
          <button onClick={conciliar} style={{ ...sx.btnGreen, alignSelf: 'flex-end', padding: '0.6rem 1.25rem' }}>
            Conciliar seleccionados ({seleccion.size})
          </button>
        )}
      </div>

      {cuentaId && (
        <div style={{ ...sx.card, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...sx.th, width: 40 }}>
                <input type="checkbox" checked={seleccion.size === pendientes.length && pendientes.length > 0} onChange={toggleAll} />
              </th>
              {['Fecha','Tipo','Monto','Descripción','Referencia'].map(h => <th key={h} style={sx.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>Cargando...</td></tr>
              ) : pendientes.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#166534', fontWeight: 700 }}>No hay movimientos pendientes de conciliación</td></tr>
              ) : pendientes.map((m, i) => (
                <tr key={m.id} style={{ background: seleccion.has(m.id) ? '#f0f4ff' : i % 2 === 0 ? '#fff' : '#f8f9fa', cursor: 'pointer' }} onClick={() => toggle(m.id)}>
                  <td style={{ ...sx.td, textAlign: 'center' }}>
                    <input type="checkbox" checked={seleccion.has(m.id)} onChange={() => toggle(m.id)} onClick={e => e.stopPropagation()} />
                  </td>
                  <td style={sx.td}>{fmtF(m.fecha)}</td>
                  <td style={{ ...sx.td, fontWeight: 700, color: colorTipo(m.tipo) }}>{TIPOS_MOV.find(t => t.key === m.tipo)?.label || m.tipo}</td>
                  <td style={{ ...sx.td, textAlign: 'right', fontWeight: 700, color: colorTipo(m.tipo) }}>{fmtM(m.monto)}</td>
                  <td style={sx.td}>{m.descripcion || '—'}</td>
                  <td style={{ ...sx.td, fontFamily: 'monospace', fontSize: '0.78rem' }}>{m.referencia || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {pendientes.length > 0 && (
            <div style={{ padding: '0.75rem 1rem', background: '#f8f9fa', borderTop: '1px solid #e9ecef', fontSize: '0.85rem', color: '#6c757d' }}>
              {pendientes.length} movimiento(s) pendiente(s) · Total: {fmtM(pendientes.reduce((s, m) => s + parseFloat(m.monto), 0))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
